import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { KeyRound, Loader2, Lock } from "lucide-react";

// A7b (I.160): entrada pela senha da conferência para quem está logado sem a chave de conferência.
// O banco responde sempre com resultado (a tentativa errada fica gravada); a tela só mostra a mensagem dele.

interface EnterResult {
  success: boolean;
  error?: string;
  mode?: "chave" | "senha";
  blocked?: boolean;
  attempts_left?: number;
}

interface StockChecklistPasswordEntryProps {
  token: string;
  checklistName: string;
  blocked: boolean;
  onEntered: () => void;
}

export const StockChecklistPasswordEntry = ({ token, checklistName, blocked, onEntered }: StockChecklistPasswordEntryProps) => {
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [isBlocked, setIsBlocked] = useState(blocked);

  const enterMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc("enter_stock_checklist_password", {
        p_token: token,
        p_password: password,
      });
      if (error) throw error;
      return data as unknown as EnterResult;
    },
    onSuccess: (result) => {
      if (result.success) {
        setMessage(null);
        onEntered();
        return;
      }
      setMessage(result.error ?? "Não foi possível entrar");
      if (result.blocked) setIsBlocked(true);
      setPassword("");
    },
    onError: (err) => {
      setMessage((err as { message?: string })?.message || "Não foi possível entrar");
    },
  });

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-6 space-y-4">
          <div className="text-center">
            <KeyRound className="h-12 w-12 text-primary mx-auto mb-3" />
            <h2 className="text-lg font-semibold">Senha da conferência</h2>
            <p className="text-sm text-muted-foreground">{checklistName}</p>
          </div>
          <p className="text-sm text-muted-foreground">
            Esta conferência é contada com senha. Digite os 6 números que quem criou a conferência passou.
          </p>
          {isBlocked && (
            <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-700">
              <Lock className="h-4 w-4 mt-0.5 shrink-0" />
              <span>Senha bloqueada depois de 5 tentativas erradas. Peça a quem criou a conferência para gerar uma nova senha.</span>
            </div>
          )}
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (!enterMutation.isPending) enterMutation.mutate();
            }}
          >
            <Input
              value={password}
              onChange={(e) => setPassword(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              className="text-center text-2xl tracking-[0.5em] font-mono"
              aria-label="Senha da conferência"
            />
            {message && <p className="text-sm text-destructive">{message}</p>}
            <Button type="submit" className="w-full" disabled={enterMutation.isPending || password.length === 0}>
              {enterMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Entrar
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
